import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmSignIn, signIn } from 'aws-amplify/auth';
import { FieldState } from '../types/loginTypes';
import { AUTH_OTP_ERROR_MESSAGES } from '../errors/translations/errorMessages';

// ============================================================================
// Types
// ============================================================================

interface UseOtpFormProps {
  email: string;
  onBack?: (() => void) | undefined;
  onResendSuccess?: (() => void) | undefined;
}

export interface UseOtpFormReturn {
  // State
  otpInputValue: FieldState;
  isSubmitting: boolean;
  isResending: boolean;
  resendTimer: number;

  // Handlers
  handleSubmit: (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
  ) => void;
  handleResendCode: () => void;
  handleOtpChange: (value: string) => void;
  handleBack: () => void;

  // Utilities
  formatTime: (seconds: number) => string;
  maskedEmail: string;
}

// ============================================================================
// Utility Functions
// ============================================================================

const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) {
    return email;
  }

  const maskedLocal =
    localPart.length > 2
      ? `${localPart.slice(0, 2)}${'*'.repeat(Math.min(localPart.length - 2, 3))}`
      : localPart;

  const domainParts = domain.split('.');
  const maskedDomain =
    domainParts[0] && domainParts[0].length > 2
      ? `${domainParts[0].slice(0, 2)}${'*'.repeat(Math.min(domainParts[0].length - 2, 4))}.${domainParts.slice(1).join('.')}`
      : domain;

  return `${maskedLocal}@${maskedDomain}`;
};

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// ============================================================================
// Hook
// ============================================================================

export const useOtpForm = ({
  email,
  onBack,
  onResendSuccess,
}: UseOtpFormProps): UseOtpFormReturn => {
  const navigate = useNavigate();

  // State
  const [resendTimer, setResendTimer] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpInputValue, setOtpInputValue] = useState<FieldState>({
    hasError: false,
    errorMessage: '',
    value: '',
  });

  // Masked email
  const maskedEmail = maskEmail(email);

  // Handle OTP input change
  const handleOtpChange = useCallback((value: string) => {
    setOtpInputValue((prev) => ({
      ...prev,
      value,
      hasError: false,
      errorMessage: '',
    }));
  }, []);

  // Handle back button
  const handleBack = useCallback(() => {
    onBack?.();
  }, [onBack]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (
      e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
    ) => {
      e.preventDefault();

      if (otpInputValue.value.length !== 6) {
        setOtpInputValue((prev) => ({
          ...prev,
          hasError: true,
          errorMessage: AUTH_OTP_ERROR_MESSAGES.INVALID_OTP.polish,
        }));
        return;
      }

      setIsSubmitting(true);

      try {
        const result = await confirmSignIn({
          challengeResponse: otpInputValue.value,
        });

        // Check if sign-in is complete
        if (result.isSignedIn) {
          navigate('/Panel-główny', {
            replace: true,
            state: { showVerifySuccess: true },
          });
        } else if (result.nextStep) {
          // Reached on any retryable failed attempt (wrong digits, or the
          // right digits after the OTP's TTL passed) — verify_auth_challenge.ts
          // reports both as answerCorrect: false with no way to tell them
          // apart, so we show one generic incorrect-code message for both.
          setOtpInputValue((prev) => ({
            ...prev,
            hasError: true,
            errorMessage: AUTH_OTP_ERROR_MESSAGES.INVALID_OTP.polish,
          }));
          setIsSubmitting(false);
        }
      } catch (_error) {
        const errorMessage = AUTH_OTP_ERROR_MESSAGES.INVALID_OTP.polish;
        setOtpInputValue((prev) => ({
          ...prev,
          hasError: true,
          errorMessage,
        }));
        setIsSubmitting(false);
      }
    },
    [otpInputValue.value, navigate]
  );

  // Handle resend code - Re-initiate signIn to trigger new OTP for Custom Auth flow
  const handleResendCode = useCallback(async () => {
    if (resendTimer > 0 || isResending) {
      return;
    }

    setIsResending(true);

    try {
      // For Custom Auth flow, resend OTP by re-initiating signIn
      // This triggers the Create Auth Challenge Lambda to send a new code
      await signIn({
        username: email.trim().toLowerCase(),
        options: {
          authFlowType: 'CUSTOM_WITHOUT_SRP',
        },
      });
      // Start 1 minute timer (60 seconds)
      setResendTimer(60);
      // Clear any previous error
      setOtpInputValue((prev) => ({
        ...prev,
        hasError: false,
        errorMessage: '',
        value: '',
      }));
      // Notify success
      onResendSuccess?.();
    } catch (_error) {
      console.error('error', _error);
      setOtpInputValue((prev) => ({
        ...prev,
        hasError: true,
        errorMessage: 'Nie udało się wysłać kodu. Spróbuj ponownie.',
      }));
    } finally {
      setIsResending(false);
    }
  }, [resendTimer, isResending, email, onResendSuccess]);

  // Countdown effect
  useEffect(() => {
    if (resendTimer <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setResendTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  return {
    // State
    otpInputValue,
    isSubmitting,
    isResending,
    resendTimer,

    // Handlers
    handleSubmit,
    handleResendCode,
    handleOtpChange,
    handleBack,

    // Utilities
    formatTime,
    maskedEmail,
  };
};
